import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageContainer, PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Search, Wallet, Receipt, Upload, Download, FileDown, X,
  History, Ban, Plus, AlertCircle, BarChart2, TrendingDown,
} from "lucide-react";
import { useConfirm } from "@/components/ConfirmDialog";
import { useAuth } from "@/contexts/AuthContext";

const money = (n) =>
  `RD$ ${Number(n || 0).toLocaleString("es-DO", {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`;

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

// ─────────────────────────────── PAGE ───────────────────────────────
export default function CxcMigration() {
  const [summary, setSummary] = useState(null);
  const [tab, setTab] = useState("clientes");
  const confirm = useConfirm();

  const loadSummary = async () => {
    try {
      const { data } = await api.get("/cxc-migration/summary");
      setSummary(data);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { loadSummary(); }, []);

  return (
    <PageContainer>
      <PageHeader
        title="CxC Migración"
        subtitle="Cuentas por cobrar históricas del sistema anterior (sin valor fiscal, no genera NCF)."
        testid="cxc-migration-header"
      />

      {/* KPI cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <KpiCard label="Clientes" value={summary.clientes_con_saldo_migrado} testid="kpi-clientes" />
          <KpiCard label="Saldo inicial total" value={money(summary.saldo_inicial_total)} testid="kpi-inicial" />
          <KpiCard label="Total cobrado" value={money(summary.total_cobrado)} testid="kpi-cobrado" />
          <KpiCard label="Saldo pendiente" value={money(summary.saldo_pendiente_total)} testid="kpi-pendiente" highlight />
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="clientes" data-testid="tab-clientes"><Wallet className="w-4 h-4 mr-1.5" />Clientes con saldo</TabsTrigger>
          <TabsTrigger value="aging" data-testid="tab-aging"><BarChart2 className="w-4 h-4 mr-1.5" />Antigüedad</TabsTrigger>
          <TabsTrigger value="recibos" data-testid="tab-recibos"><Receipt className="w-4 h-4 mr-1.5" />Historial de recibos</TabsTrigger>
          <TabsTrigger value="import" data-testid="tab-import"><Upload className="w-4 h-4 mr-1.5" />Importar CSV</TabsTrigger>
        </TabsList>

        <TabsContent value="clientes"><ClientesTab onChange={loadSummary} confirm={confirm} /></TabsContent>
        <TabsContent value="aging"><AgingTab /></TabsContent>
        <TabsContent value="recibos"><RecibosTab onChange={loadSummary} confirm={confirm} /></TabsContent>
        <TabsContent value="import"><ImportTab onChange={loadSummary} /></TabsContent>
      </Tabs>
    </PageContainer>
  );
}

function KpiCard({ label, value, testid, highlight }) {
  return (
    <div className="bg-card border border-border rounded-md p-3" data-testid={testid}>
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`text-2xl font-bold mt-1 ${highlight ? "text-primary" : ""}`}>{value}</div>
    </div>
  );
}

// ─────────────────────────────── CLIENTES TAB ───────────────────────────────
function ClientesTab({ onChange, confirm }) {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [onlyPending, setOnlyPending] = useState(true);
  const [openOpening, setOpenOpening] = useState(false);
  const [openPayment, setOpenPayment] = useState(false);
  const [editing, setEditing] = useState(null);
  const { user } = useAuth();

  const load = async () => {
    try {
      const { data } = await api.get("/cxc-migration/customers", {
        params: { q: q || undefined, only_pending: onlyPending },
      });
      setItems(data);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [q, onlyPending]);

  const canPrivileged = ["admin", "gerente"].includes((user?.role || "").toLowerCase());

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2 flex-1 min-w-[240px]">
          <Search className="w-4 h-4 text-muted-foreground" />
          <Input
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por nombre, RNC, teléfono o email…"
            className="h-8 max-w-md" data-testid="cxc-migration-search"
          />
          {q && <Button size="sm" variant="ghost" onClick={() => setQ("")}><X className="w-4 h-4" /></Button>}
        </div>
        <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="only-pending-toggle">
          <input type="checkbox" checked={onlyPending} onChange={(e) => setOnlyPending(e.target.checked)} />
          Solo con saldo pendiente
        </label>
        <Button
          onClick={() => { setEditing(null); setOpenOpening(true); }}
          className="bg-primary font-bold" data-testid="new-opening-btn"
        >
          <Plus className="w-4 h-4 mr-1.5" /> Nuevo cliente migrado
        </Button>
      </div>

      <div className="bg-card border border-border rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="overline border-b border-border">
            <tr>
              <th className="text-left p-3">Cliente</th>
              <th className="text-left p-3">RNC / Cédula</th>
              <th className="text-left p-3">Teléfono</th>
              <th className="text-right p-3">Saldo inicial</th>
              <th className="text-right p-3">Cobrado</th>
              <th className="text-right p-3">Saldo actual</th>
              <th className="text-right p-3">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id} className="border-b border-border/60 hover:bg-accent" data-testid={`row-${c.id}`}>
                <td className="p-3 font-semibold">{c.name}</td>
                <td className="p-3 data-cell">{c.rnc_cedula || "—"}</td>
                <td className="p-3 data-cell">{c.phone || "—"}</td>
                <td className="p-3 text-right data-cell">{money(c.saldo_inicial)}</td>
                <td className="p-3 text-right data-cell">{money(c.total_cobrado)}</td>
                <td className={`p-3 text-right data-cell font-bold ${c.saldo_actual > 0 ? "text-primary" : "text-muted-foreground"}`}>
                  {money(c.saldo_actual)}
                </td>
                <td className="p-3 text-right whitespace-nowrap">
                  <Button
                    size="sm" variant="outline"
                    disabled={c.saldo_actual <= 0}
                    onClick={() => { setEditing(c); setOpenPayment(true); }}
                    data-testid={`pay-${c.id}`}
                    title={c.saldo_actual <= 0 ? "Sin saldo pendiente" : "Registrar cobro"}
                  >
                    <Receipt className="w-4 h-4 mr-1" /> Cobrar
                  </Button>
                  {canPrivileged && (
                    <Button
                      size="sm" variant="ghost" className="ml-1"
                      onClick={() => { setEditing(c); setOpenOpening(true); }}
                      data-testid={`edit-${c.id}`}
                      title="Ajustar saldo inicial (admin/gerente)"
                    >
                      Ajustar
                    </Button>
                  )}
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">
                Sin clientes con saldo migrado
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <OpeningDialog
        open={openOpening} onOpenChange={setOpenOpening}
        editing={editing}
        onSaved={() => { setOpenOpening(false); setEditing(null); load(); onChange(); }}
      />
      <PaymentDialog
        open={openPayment} onOpenChange={setOpenPayment}
        customer={editing}
        onSaved={() => { setOpenPayment(false); setEditing(null); load(); onChange(); }}
      />
    </div>
  );
}

// ─────────────────────────────── OPENING DIALOG ───────────────────────────────
function OpeningDialog({ open, onOpenChange, editing, onSaved }) {
  const [customers, setCustomers] = useState([]);
  const [form, setForm] = useState({
    customer_id: "", name: "", rnc_cedula: "", phone: "", email: "", address: "",
    saldo_inicial: 0, detalle_migracion: "", fecha_corte: todayISO(),
  });
  const [saving, setSaving] = useState(false);
  const isEditing = !!editing;

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({
        customer_id: editing.id,
        name: editing.name,
        rnc_cedula: editing.rnc_cedula || "",
        phone: editing.phone || "",
        email: editing.email || "",
        address: "",
        saldo_inicial: editing.saldo_inicial || 0,
        detalle_migracion: editing.detalle_migracion || "",
        fecha_corte: editing.fecha_corte_migracion || todayISO(),
      });
    } else {
      setForm({
        customer_id: "", name: "", rnc_cedula: "", phone: "", email: "", address: "",
        saldo_inicial: 0, detalle_migracion: "", fecha_corte: todayISO(),
      });
      // Load ALL customers to allow linking existing ones without balance yet
      api.get("/customers").then(({ data }) => setCustomers(data)).catch(() => {});
    }
  }, [open, editing]);

  const onSubmit = async (e) => {
    e.preventDefault();
    const saldo = parseFloat(form.saldo_inicial);
    if (!(saldo >= 0)) return toast.error("Saldo inválido");
    setSaving(true);
    try {
      let customer_id = form.customer_id;
      if (!isEditing && !customer_id) {
        // Crear cliente nuevo con saldo migrado
        if (!form.name.trim()) { setSaving(false); return toast.error("Nombre requerido"); }
        const { data } = await api.post("/customers", {
          name: form.name, rnc_cedula: form.rnc_cedula, phone: form.phone,
          email: form.email, address: form.address, notes: "", active: true,
          credit_days: 0, product_types: [],
          saldo_inicial_cxc: saldo,
          detalle_migracion: form.detalle_migracion,
          fecha_corte_migracion: form.fecha_corte,
        });
        customer_id = data.id;
      } else {
        // Set opening (ajuste o vincular cliente existente)
        await api.post("/cxc-migration/opening", {
          customer_id,
          saldo_inicial: saldo,
          detalle_migracion: form.detalle_migracion,
          fecha_corte: form.fecha_corte,
        });
      }
      toast.success(isEditing ? "Saldo ajustado" : "Cliente migrado creado");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{isEditing ? "Ajustar saldo migrado" : "Nuevo cliente con saldo migrado"}</DialogTitle>
          <DialogDescription>
            {isEditing
              ? "Modifica el saldo inicial migrado. Se regenerará el asiento contable de apertura."
              : "Ingresa el cliente con el saldo pendiente arrastrado del sistema anterior. Se generará el asiento de apertura contable."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="grid grid-cols-2 gap-3">
          {!isEditing && (
            <>
              <div className="col-span-2">
                <Label>¿Es un cliente nuevo o existente?</Label>
                <Select
                  value={form.customer_id ? "existing" : "new"}
                  onValueChange={(v) => setForm({ ...form, customer_id: v === "new" ? "" : (customers[0]?.id || "") })}
                >
                  <SelectTrigger className="mt-1" data-testid="opening-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">Crear cliente nuevo</SelectItem>
                    <SelectItem value="existing">Vincular a cliente existente</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {form.customer_id === "" ? (
                <>
                  <div><Label>Nombre *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" required data-testid="opening-name" /></div>
                  <div><Label>RNC / Cédula</Label><Input value={form.rnc_cedula} onChange={(e) => setForm({ ...form, rnc_cedula: e.target.value })} className="mt-1" /></div>
                  <div><Label>Teléfono</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1" /></div>
                  <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" /></div>
                  <div className="col-span-2"><Label>Dirección</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1" /></div>
                </>
              ) : (
                <div className="col-span-2">
                  <Label>Cliente existente</Label>
                  <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })}>
                    <SelectTrigger className="mt-1" data-testid="opening-customer-select"><SelectValue placeholder="Selecciona un cliente" /></SelectTrigger>
                    <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} {c.rnc_cedula ? `· ${c.rnc_cedula}` : ""}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
            </>
          )}

          {isEditing && (
            <div className="col-span-2 bg-accent/40 p-3 rounded text-sm" data-testid="editing-info">
              <b>Cliente:</b> {form.name} {form.rnc_cedula ? `· RNC ${form.rnc_cedula}` : ""}
            </div>
          )}

          <div>
            <Label>Saldo inicial (RD$) *</Label>
            <Input
              type="number" step="0.01" min="0"
              value={form.saldo_inicial}
              onChange={(e) => setForm({ ...form, saldo_inicial: e.target.value })}
              className="mt-1" required data-testid="opening-saldo"
            />
          </div>
          <div>
            <Label>Fecha de corte (asiento contable)</Label>
            <Input
              type="date" value={form.fecha_corte}
              onChange={(e) => setForm({ ...form, fecha_corte: e.target.value })}
              className="mt-1" data-testid="opening-fecha"
            />
          </div>
          <div className="col-span-2">
            <Label>Detalle de migración (opcional)</Label>
            <Textarea
              value={form.detalle_migracion}
              onChange={(e) => setForm({ ...form, detalle_migracion: e.target.value })}
              placeholder="Ej: Balance según libro auxiliar al 31-dic-2025"
              rows={2} className="mt-1" data-testid="opening-detalle"
            />
          </div>

          <div className="col-span-2 flex items-start gap-2 p-3 bg-orange-50 dark:bg-orange-950/20 border border-orange-200 dark:border-orange-800 rounded text-xs text-orange-900 dark:text-orange-200">
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>Este saldo no genera factura, NCF ni ITBIS. Se registra únicamente como apertura contable (Débito CxC / Crédito Ajuste de Apertura).</span>
          </div>

          <DialogFooter className="col-span-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" disabled={saving} className="bg-primary font-bold" data-testid="opening-save">
              {saving ? "Guardando…" : "Guardar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────── PAYMENT DIALOG ───────────────────────────────
function PaymentDialog({ open, onOpenChange, customer, onSaved }) {
  const [form, setForm] = useState({ amount: "", payment_method: "efectivo", reference: "", notes: "", date: todayISO() });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({ amount: "", payment_method: "efectivo", reference: "", notes: "", date: todayISO() });
    }
  }, [open]);

  if (!customer) return null;

  const onSubmit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(form.amount);
    if (!(amt > 0)) return toast.error("Monto debe ser mayor a 0");
    if (amt > (customer.saldo_actual || 0) + 0.005)
      return toast.error(`Monto excede el saldo pendiente (${money(customer.saldo_actual)})`);

    setSaving(true);
    try {
      const { data } = await api.post("/cxc-migration/payments", {
        customer_id: customer.id,
        amount: amt,
        payment_method: form.payment_method,
        reference: form.reference,
        notes: form.notes,
        date: form.date,
      });
      toast.success(`Cobro registrado: ${data.number}`);
      // Auto-descarga PDF
      try {
        const pdf = await api.get(`/cxc-migration/payments/${data.id}/pdf`, { responseType: "blob" });
        const url = URL.createObjectURL(pdf.data);
        const a = document.createElement("a");
        a.href = url; a.download = `${data.number}.pdf`; a.click();
        URL.revokeObjectURL(url);
      } catch { /* silencioso */ }
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Registrar cobro — {customer.name}</DialogTitle>
          <DialogDescription>
            Saldo pendiente: <b className="text-primary">{money(customer.saldo_actual)}</b>
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-3">
          <div>
            <Label>Monto a cobrar (RD$)</Label>
            <Input
              type="number" step="0.01" min="0.01" max={customer.saldo_actual}
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              className="mt-1" required autoFocus data-testid="payment-amount"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Método de pago</Label>
              <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
                <SelectTrigger className="mt-1" data-testid="payment-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="efectivo">Efectivo</SelectItem>
                  <SelectItem value="transferencia">Transferencia</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                  <SelectItem value="tarjeta">Tarjeta</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Fecha</Label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1" />
            </div>
          </div>
          <div>
            <Label>Referencia (opcional)</Label>
            <Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} className="mt-1" placeholder="No. cheque / transferencia…" />
          </div>
          <div>
            <Label>Notas (opcional)</Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="mt-1" />
          </div>

          <div className="flex items-start gap-2 p-2 bg-orange-50 dark:bg-orange-950/20 border border-orange-200 dark:border-orange-800 rounded text-xs text-orange-900 dark:text-orange-200">
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>Se generará un recibo <b>RCM-######</b> sin valor fiscal. No genera NCF ni ITBIS.</span>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" disabled={saving} className="bg-primary font-bold" data-testid="payment-save">
              {saving ? "Guardando…" : "Registrar cobro"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────── AGING TAB ───────────────────────────────
const BUCKET_META = {
  "0-30":  { label: "0 – 30 días",  color: "text-emerald-700 dark:text-emerald-400", bg: "bg-emerald-500/10 border-emerald-500/40" },
  "31-60": { label: "31 – 60 días", color: "text-amber-700 dark:text-amber-400",     bg: "bg-amber-500/10 border-amber-500/40" },
  "61-90": { label: "61 – 90 días", color: "text-orange-700 dark:text-orange-400",   bg: "bg-orange-500/10 border-orange-500/40" },
  "+90":   { label: "Más de 90 días", color: "text-red-700 dark:text-red-400",       bg: "bg-red-500/10 border-red-500/40" },
};
const BUCKET_ORDER = ["0-30", "31-60", "61-90", "+90"];

function AgingTab() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    try {
      const { data } = await api.get("/cxc-migration/aging");
      setData(data);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { load(); }, []);

  const filteredItems = useMemo(() => {
    if (!data) return [];
    if (filter === "all") return data.items;
    return data.items.filter((it) => it.aging_bucket === filter);
  }, [data, filter]);

  const exportCsv = () => {
    if (!data) return;
    const rows = [
      ["Bucket", "Cliente", "RNC/Cédula", "Teléfono", "Fecha corte", "Días", "Saldo pendiente"].join(","),
      ...data.items.map((it) => [
        it.aging_bucket,
        `"${(it.name || "").replace(/"/g, '""')}"`,
        it.rnc_cedula || "",
        it.phone || "",
        it.fecha_corte || "",
        it.days_overdue,
        Number(it.saldo_pendiente).toFixed(2),
      ].join(",")),
    ];
    const blob = new Blob(["\ufeff" + rows.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `antiguedad_cxc_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!data) return <div className="text-center p-8 text-muted-foreground" data-testid="aging-loading">Cargando…</div>;

  return (
    <div className="space-y-4">
      {/* Buckets cards — clickable filter */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {BUCKET_ORDER.map((b) => {
          const meta = BUCKET_META[b];
          const active = filter === b;
          return (
            <button
              key={b}
              onClick={() => setFilter(active ? "all" : b)}
              className={`text-left border rounded-md p-3 transition-all ${meta.bg} ${active ? "ring-2 ring-primary" : ""}`}
              data-testid={`aging-bucket-${b}`}
            >
              <div className={`text-xs uppercase tracking-wide font-bold ${meta.color}`}>{meta.label}</div>
              <div className="text-2xl font-black tracking-tight mt-1">{money(data.buckets[b])}</div>
              <div className="text-xs text-muted-foreground mt-1">
                {data.counts[b]} cliente{data.counts[b] === 1 ? "" : "s"}
              </div>
            </button>
          );
        })}
      </div>

      {/* Total + actions */}
      <div className="flex flex-wrap items-center gap-2 justify-between bg-card border border-border rounded-md p-3">
        <div>
          <span className="text-sm text-muted-foreground">Total pendiente migrado: </span>
          <span className="text-xl font-black text-primary" data-testid="aging-total">{money(data.total_pendiente)}</span>
          {filter !== "all" && (
            <span className="ml-3 text-xs px-2 py-0.5 rounded bg-primary/10 text-primary font-medium">
              Filtro: {BUCKET_META[filter].label}
              <button onClick={() => setFilter("all")} className="ml-1"><X className="w-3 h-3 inline" /></button>
            </span>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load}>Actualizar</Button>
          <Button variant="outline" size="sm" onClick={exportCsv} data-testid="aging-export">
            <Download className="w-4 h-4 mr-1" /> Exportar CSV
          </Button>
        </div>
      </div>

      {/* Detalle por cliente */}
      <div className="bg-card border border-border rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="overline border-b border-border">
            <tr>
              <th className="text-left p-3">Bucket</th>
              <th className="text-left p-3">Cliente</th>
              <th className="text-left p-3">RNC / Cédula</th>
              <th className="text-left p-3">Fecha corte</th>
              <th className="text-right p-3">Días</th>
              <th className="text-right p-3">Saldo pendiente</th>
            </tr>
          </thead>
          <tbody>
            {filteredItems.map((it) => (
              <tr key={it.customer_id} className="border-b border-border/60 hover:bg-accent" data-testid={`aging-row-${it.customer_id}`}>
                <td className="p-3">
                  <span className={`text-xs px-2 py-0.5 rounded font-bold ${BUCKET_META[it.aging_bucket].bg} ${BUCKET_META[it.aging_bucket].color}`}>
                    {BUCKET_META[it.aging_bucket].label}
                  </span>
                </td>
                <td className="p-3 font-semibold">{it.name}</td>
                <td className="p-3 data-cell">{it.rnc_cedula || "—"}</td>
                <td className="p-3 data-cell">{it.fecha_corte || "—"}</td>
                <td className="p-3 text-right data-cell">{it.days_overdue}</td>
                <td className="p-3 text-right data-cell font-bold text-primary">{money(it.saldo_pendiente)}</td>
              </tr>
            ))}
            {filteredItems.length === 0 && (
              <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">Sin registros en este bucket</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─────────────────────────────── RECIBOS TAB ───────────────────────────────
function RecibosTab({ onChange, confirm }) {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const { user } = useAuth();
  const canVoid = ["admin", "gerente"].includes((user?.role || "").toLowerCase());

  const load = async () => {
    try {
      const { data } = await api.get("/cxc-migration/payments", { params: { limit: 500 } });
      setItems(data);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!q.trim()) return items;
    const s = q.toLowerCase();
    return items.filter((r) =>
      (r.number || "").toLowerCase().includes(s) ||
      (r.customer_name || "").toLowerCase().includes(s) ||
      (r.customer_rnc_cedula || "").toLowerCase().includes(s)
    );
  }, [items, q]);

  const downloadPdf = async (r) => {
    try {
      const { data } = await api.get(`/cxc-migration/payments/${r.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = `${r.number}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const voidReceipt = async (r) => {
    const ok = await confirm({
      title: `Anular recibo ${r.number}?`,
      description: "Se generará un asiento contable inverso. El monto volverá al saldo pendiente del cliente.",
      confirmLabel: "Anular",
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/cxc-migration/payments/${r.id}`);
      toast.success("Recibo anulado");
      load(); onChange();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Search className="w-4 h-4 text-muted-foreground" />
        <Input
          value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar por número, cliente o RNC…"
          className="h-8 max-w-md" data-testid="recibos-search"
        />
        {q && <Button size="sm" variant="ghost" onClick={() => setQ("")}><X className="w-4 h-4" /></Button>}
        <span className="text-xs text-muted-foreground ml-auto">{filtered.length} recibos</span>
      </div>

      <div className="bg-card border border-border rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="overline border-b border-border">
            <tr>
              <th className="text-left p-3">Número</th>
              <th className="text-left p-3">Fecha</th>
              <th className="text-left p-3">Cliente</th>
              <th className="text-left p-3">Método</th>
              <th className="text-right p-3">Monto</th>
              <th className="text-right p-3">Saldo pendiente</th>
              <th className="text-left p-3">Estado</th>
              <th className="text-right p-3">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.id} className={`border-b border-border/60 hover:bg-accent ${r.status === "anulado" ? "opacity-50" : ""}`} data-testid={`receipt-${r.number}`}>
                <td className="p-3 font-bold data-cell">{r.number}</td>
                <td className="p-3 data-cell">{(r.date || "").slice(0, 10)}</td>
                <td className="p-3">{r.customer_name}</td>
                <td className="p-3 uppercase text-xs">{r.payment_method}</td>
                <td className="p-3 text-right data-cell font-bold">{money(r.amount)}</td>
                <td className="p-3 text-right data-cell">{money(r.saldo_pendiente)}</td>
                <td className="p-3">
                  {r.status === "anulado"
                    ? <span className="text-xs px-2 py-0.5 rounded bg-destructive/10 text-destructive">ANULADO</span>
                    : <span className="text-xs px-2 py-0.5 rounded bg-green-500/10 text-green-700 dark:text-green-400">Activo</span>
                  }
                </td>
                <td className="p-3 text-right whitespace-nowrap">
                  <Button size="sm" variant="ghost" onClick={() => downloadPdf(r)} title="Descargar PDF" data-testid={`pdf-${r.number}`}>
                    <FileDown className="w-4 h-4" />
                  </Button>
                  {r.status !== "anulado" && canVoid && (
                    <Button size="sm" variant="ghost" onClick={() => voidReceipt(r)} title="Anular recibo" data-testid={`void-${r.number}`}>
                      <Ban className="w-4 h-4 text-destructive" />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={8} className="p-8 text-center text-muted-foreground">Sin recibos registrados</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─────────────────────────────── IMPORT TAB ───────────────────────────────
function ImportTab({ onChange }) {
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const downloadTemplate = async () => {
    try {
      const { data } = await api.get("/cxc-migration/import-csv/template", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = "plantilla_migracion_cxc.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Selecciona un archivo CSV");
    const fd = new FormData();
    fd.append("file", file);
    setLoading(true); setResult(null);
    try {
      const { data } = await api.post("/cxc-migration/import-csv", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success(`Import completado: ${data.created} nuevos, ${data.updated} actualizados`);
      onChange();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-4">
      <div className="bg-card border border-border rounded-md p-4">
        <h3 className="font-bold mb-2">Importar CxC migrada desde CSV</h3>
        <p className="text-sm text-muted-foreground mb-3">
          Sube un archivo CSV con los clientes y sus saldos pendientes del sistema anterior.
          Se creará el asiento contable de apertura automáticamente por cada uno.
        </p>

        <div className="flex flex-wrap gap-3 mb-3">
          <Button variant="outline" onClick={downloadTemplate} data-testid="download-template">
            <Download className="w-4 h-4 mr-1.5" /> Descargar plantilla
          </Button>
        </div>

        <details className="text-xs text-muted-foreground border border-border rounded p-3 mb-3">
          <summary className="cursor-pointer font-semibold">Ver columnas del CSV</summary>
          <ul className="mt-2 space-y-1">
            <li><b>nombre</b> (obligatorio)</li>
            <li><b>saldo_inicial</b> (obligatorio, numérico, separador punto)</li>
            <li>rnc_cedula, telefono, email, direccion (opcionales)</li>
            <li><b>fecha_corte</b> (YYYY-MM-DD, opcional pero recomendado)</li>
            <li>detalle_migracion (opcional)</li>
          </ul>
          <p className="mt-2">Si el cliente ya existe (por RNC o por nombre exacto), se actualizará su saldo migrado. Si ya tiene cobros aplicados, solo admin/gerente puede modificarlo.</p>
        </details>

        <form onSubmit={submit} className="flex flex-wrap items-center gap-2">
          <input
            type="file" accept=".csv"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="text-sm" data-testid="csv-file-input"
          />
          <Button type="submit" disabled={!file || loading} className="bg-primary font-bold" data-testid="csv-submit">
            {loading ? "Importando…" : <><Upload className="w-4 h-4 mr-1.5" />Importar</>}
          </Button>
        </form>
      </div>

      {result && (
        <div className="bg-card border border-border rounded-md p-4" data-testid="import-result">
          <div className="grid grid-cols-3 gap-3 mb-3">
            <KpiCard label="Creados" value={result.created} testid="import-created" />
            <KpiCard label="Actualizados" value={result.updated} testid="import-updated" />
            <KpiCard label="Omitidos" value={result.skipped} testid="import-skipped" highlight={result.skipped > 0} />
          </div>
          {result.errors && result.errors.length > 0 && (
            <div>
              <h4 className="font-bold text-sm mb-2 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" /> Errores ({result.errors.length})
              </h4>
              <div className="max-h-48 overflow-y-auto border border-border rounded p-2 space-y-1 text-xs">
                {result.errors.map((er, i) => (
                  <div key={i}><b>Fila {er.row}:</b> {er.error}</div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
